"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { getDefaultWidgetInstance, getPublicKey, type WidgetInstance } from "./apollo-client";
import {
  useStartAnonymousVisitorMutation,
  useStartIdentifiedVisitorMutation,
} from "./graphql/queries/generated/messaging.generated";
import { detectClientLocale } from "./locale-detect";
import { isTrustedMessageSource } from "./message-source";
import { usePreview } from "./preview/use-preview";
import { computeRenewDelay, MAX_TIMER_MS } from "./renew-delay";
import type { VisitorIdentity, VisitorPrefill, WidgetEnv } from "./use-widget-env";
import { browserStorage, type VisitorStorage } from "./visitor-storage";
import { type GuardErrorLike, hasGuardCode, WEB_GUARD_CODES } from "./web-guard-codes";
import { useWidgetInstance } from "./widget-instance-context";

/**
 * Visitor session — calls `startAnonymousVisitor` on first mount and
 * stashes the JWT into `apollo-client`'s module-scoped slot so every
 * subsequent query/mutation stamps `Authorization: Bearer <token>`.
 *
 * **Visitor id is sticky** in `localStorage["wexio:visitor-id"]` so
 * returning visitors land in the same chat across reloads + tabs.
 * Generated as a random uuid on first visit.
 *
 * **Gating**:
 *   - Skips entirely when `enabled === false` (demo + preview modes
 *     where there's no real BE integration to authenticate against).
 *   - One-shot per mount — no auto re-auth here. JWT expiry handling
 *     comes in Day 2g (silent renew ~30s before `expiresIn`).
 *
 * **Errors are non-fatal**: a failed handshake leaves the widget in
 * "anonymous, unauthenticated" mode — Help + News still work
 * (anonymous-readable), but chat history + send mutations 4xx until
 * the handshake succeeds. Errors are surfaced to the caller; UI
 * decides whether to retry / show banner.
 */
/** `session.error` sentinel for "the BE was unreachable" (server down /
 *  network), as opposed to a real auth failure. The shell treats it as an
 *  offline state (no alarming sign-in overlay) and keeps retrying. */
export const NETWORK_UNREACHABLE = "network_unreachable";

/** `session.error` sentinel: a preview already minted its quota of new test visitors (BE 429
 *  `WEB_PREVIEW_VISITOR_LIMIT`). TERMINAL for the mount: never retried, never polled. */
export const PREVIEW_VISITOR_LIMIT = "preview_visitor_limit";

/** Was a handshake rejection a connectivity failure (unreachable BE) rather
 *  than a real auth error? Apollo surfaces a link/transport failure as a
 *  `networkError` with no `graphQLErrors`; older/plain errors fall back to a
 *  message probe (fetch/network wording, incl. `ERR_CONNECTION*`). */
function isNetworkError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as {
    networkError?: unknown;
    graphQLErrors?: unknown[];
    message?: unknown;
  };
  if (Array.isArray(e.graphQLErrors) && e.graphQLErrors.length > 0) return false;
  if (e.networkError) return true;
  const msg = typeof e.message === "string" ? e.message : "";
  return /failed to fetch|networkerror|network request failed|load failed|err_|fetch/i.test(msg);
}

export interface VisitorSession {
  token: string | null;
  chatId: string | null;
  peopleId: string | null;
  displayName: string | null;
  isAuthenticating: boolean;
  error: string | null;
  /** `true` once the BE confirms a VERIFIED-identified visitor
   *  (`kind === 'identified'`). Sensitive UI should gate on this — never
   *  on a passed email. `false` for anonymous / prefilled visitors. */
  verified: boolean;
  /** GLOBAL (contact-level) block from the handshake (`VisitorAuthResult.blocked`)
   *  — the FIRST-PAINT gate so a blocked visitor never sees a live composer even
   *  in the handshake→threads window. The ongoing source is
   *  `visitorThreads.visitorBlocked` + the live CONTACT stream. */
  blocked: boolean;
}

/** Common subset of the `VisitorAuthResult` both handshakes return. */
type HandshakeResult = {
  token: string;
  expiresIn: number;
  chatId: string;
  peopleId: string;
  /** Resolved visitor id — the BE echoes the id it actually used. It no
   *  longer rotates (that forked chats); we persist whatever comes back so
   *  the widget stays in lockstep if that ever changes again. */
  visitorId: string;
  displayName: string;
  /** `'anonymous' | 'identified'` — drives `VisitorSession.verified`. */
  kind: string;
  /** Contact-level block flag (`VisitorAuthResult.blocked`). */
  blocked: boolean;
};

/** Storage-key generators — every key is scoped by the integration's
 *  `publicKey` so two operators embedding the widget on different
 *  sites (both iframes load the SAME `widget.wexio.com` origin →
 *  shared localStorage) don't see each other's visitor state.
 *
 *  Without this scoping, operator A's visitor JWT would be read by
 *  operator B's widget on first mount, fail BE validation
 *  (publicKey mismatch in the JWT), trigger a 401 recovery, and leak
 *  audit-log noise / a brief UX flicker. Same-operator multi-domain
 *  setups intentionally still share state (same publicKey → same
 *  storage key → SSO across the operator's properties). */
const visitorIdKey = (publicKey: string | null) =>
  publicKey ? `wexio:visitor-id:${publicKey}` : "wexio:visitor-id";
const identifiedSessionKey = (publicKey: string | null) =>
  publicKey ? `wexio:visitor-session:${publicKey}` : "wexio:visitor-session";

/** Legacy (pre-publicKey-scoping) keys — read once on first mount and
 *  removed so a returning visitor's existing session is preserved for
 *  THIS integration but no longer bleeds to others on the same widget
 *  origin. */
const LEGACY_VISITOR_ID_KEY = "wexio:visitor-id";
const LEGACY_IDENTIFIED_SESSION_KEY = "wexio:visitor-session";

interface PersistedIdentifiedSession {
  token: string;
  chatId: string;
  peopleId: string;
  displayName: string;
  /** Wall-clock ms when the token expires (mint time + `expiresIn`). */
  expiresAt: number;
}

/** Persist a freshly-minted identified session so a reload restores it.
 *  Exported because the passkey ceremony (a 2-step flow outside the
 *  handshake) mints its session out-of-band and hands it to the shell. */
export function persistIdentifiedSession(
  input: {
    token: string;
    chatId: string;
    peopleId: string;
    displayName: string;
    expiresIn: number;
  },
  publicKey: string | null = getPublicKey(),
  storage: VisitorStorage = browserStorage("local"),
): void {
  if (typeof window === "undefined") return;
  try {
    const payload: PersistedIdentifiedSession = {
      token: input.token,
      chatId: input.chatId,
      peopleId: input.peopleId,
      displayName: input.displayName,
      expiresAt: Date.now() + input.expiresIn * 1000,
    };
    storage.setItem(identifiedSessionKey(publicKey), JSON.stringify(payload));
  } catch {
    /* private mode — session just won't survive reload */
  }
}

/** Read the persisted identified session if it's still valid (with a
 *  small leeway so a near-expired token isn't restored only to 401).
 *  Clears + returns null when missing / expired / malformed.
 *
 *  Performs a one-time migration of a legacy (pre-publicKey-scoping)
 *  global key — moves it under THIS publicKey so the visitor doesn't
 *  lose their session, and clears the global key so other operators
 *  don't see it on their first read. */
function readPersistedIdentifiedSession(
  pk: string | null,
  storage: VisitorStorage,
): PersistedIdentifiedSession | null {
  if (typeof window === "undefined") return null;
  try {
    let raw = storage.getItem(identifiedSessionKey(pk));
    if (!raw && pk) {
      // Migrate: legacy global key → publicKey-scoped key.
      const legacy = storage.getItem(LEGACY_IDENTIFIED_SESSION_KEY);
      if (legacy) {
        storage.setItem(identifiedSessionKey(pk), legacy);
        storage.removeItem(LEGACY_IDENTIFIED_SESSION_KEY);
        raw = legacy;
      }
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedIdentifiedSession;
    if (
      typeof parsed?.token !== "string" ||
      typeof parsed?.expiresAt !== "number" ||
      parsed.expiresAt - RENEW_LEEWAY_SECONDS * 1000 <= Date.now()
    ) {
      storage.removeItem(identifiedSessionKey(pk));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function clearPersistedIdentifiedSession(pk: string | null, storage: VisitorStorage): void {
  if (typeof window === "undefined") return;
  try {
    storage.removeItem(identifiedSessionKey(pk));
    // Belt-and-braces: also clear the legacy global key in case a
    // previous build wrote it before this migration shipped.
    storage.removeItem(LEGACY_IDENTIFIED_SESSION_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** Renew the JWT this many seconds before its declared expiry. Trades
 *  some early-renewal overhead for headroom against clock skew between
 *  the visitor's machine + the BE. */
const RENEW_LEEWAY_SECONDS = 30;
/** A preview JWT living less than this is bounded by the preview token's expiry. */
const PREVIEW_SHORT_JWT_SECONDS = 120;
/** Never silently renew a preview session more often than this. */
const PREVIEW_MIN_RENEW_MS = 30_000;

/** Prefix of the per-integration prechat-submitted flags — kept in sync
 *  with `prechatStorageKey` in the prechat helpers. Cleared on shutdown
 *  so the next visitor on a shared device is asked again. */
const PRECHAT_FLAG_PREFIX = "wexio:prechat-submitted:";

/**
 * Wipe the stored visitor session — for `shutdown` (logout / shared
 * device). Drops the in-memory token, forgets the sticky `visitorId` (so
 * the next handshake is a fresh identity, NOT the previous user's chat),
 * and clears the prechat-submitted flags. The caller still needs to
 * re-handshake (bump `epoch`) + clear the in-memory chat.
 */
export function resetStoredVisitorSession(
  instance: WidgetInstance = getDefaultWidgetInstance(),
): void {
  instance.token = null;
  const pk = instance.publicKey;
  const storage = instance.storage;
  clearPersistedIdentifiedSession(pk, storage);
  if (typeof window === "undefined") return;
  try {
    storage.removeItem(visitorIdKey(pk));
    // Also clear the legacy global key (pre-publicKey-scoping).
    storage.removeItem(LEGACY_VISITOR_ID_KEY);
    for (let i = storage.length - 1; i >= 0; i--) {
      const key = storage.key(i);
      if (key?.startsWith(PRECHAT_FLAG_PREFIX)) storage.removeItem(key);
    }
  } catch {
    /* private mode — nothing persisted to clear */
  }
}

export function useVisitorAuth(opts: {
  enabled: boolean;
  /** Known-user identity from the host. When it carries a `userId` +
   *  `userHash`, the handshake routes to `startIdentifiedVisitor` so the
   *  visitor lands as a verified, named person; otherwise it stays
   *  anonymous. May arrive AFTER mount (the iframe receives it over
   *  postMessage) — a late arrival re-handshakes on the same `visitorId`,
   *  which the BE transitions anonymous → identified. */
  identity?: VisitorIdentity | null;
  /** Bump to force a fresh handshake — used by `shutdown` after
   *  `resetStoredVisitorSession()` has cleared the old `visitorId`, so the
   *  re-handshake mints a brand-new anonymous identity. */
  epoch?: number;
  /** Optional Turnstile token provider — when the integration has
   *  Cloudflare Turnstile enabled, the shell wires up `useTurnstile`
   *  and passes its `getToken` here. The handshake awaits a fresh
   *  token before each `startAnonymous` / `startIdentified` call and
   *  forwards it as `turnstileToken`. Omit / pass a function that
   *  resolves to `undefined` when Turnstile is off — the BE accepts
   *  no token when bot-protection isn't required for the integration. */
  getTurnstileToken?: () => Promise<string | undefined>;
}): VisitorSession {
  const [session, setSession] = useState<VisitorSession>({
    token: null,
    chatId: null,
    peopleId: null,
    displayName: null,
    isAuthenticating: opts.enabled,
    error: null,
    verified: false,
    blocked: false,
  });

  const instance = useWidgetInstance();
  // BE preview (BE 062): `startAnonymousVisitor` is the ONLY auth path, so the host `user` is ignored
  // and nothing handshakes before an iframe preview has its token. A different token (the host
  // refreshed it, or replaced an expired one) drops the old visitor token in this very render and
  // re-handshakes on the SAME visitor id, which the BE resumes.
  const { token: previewToken, holding: previewHolding } = usePreview();
  const enabled = opts.enabled && !previewHolding;
  const [seenPreviewToken, setSeenPreviewToken] = useState(previewToken);
  if (seenPreviewToken !== previewToken) {
    setSeenPreviewToken(previewToken);
    instance.token = null;
    setSession({
      token: null,
      chatId: null,
      peopleId: null,
      displayName: null,
      isAuthenticating: enabled,
      error: null,
      verified: false,
      blocked: false,
    });
  }
  const [startAnonymous] = useStartAnonymousVisitorMutation();
  const [startIdentified] = useStartIdentifiedVisitorMutation();
  // Latest identity, read inside `performHandshake` without making it a
  // dependency (the effect re-fires on `identityKey` instead).
  const identityRef = useRef<VisitorIdentity | null>(opts.identity ?? null);
  identityRef.current = previewToken ? null : (opts.identity ?? null);
  // Latest Turnstile token provider — same ref pattern as identity so
  // the handshake `useCallback` stays stable when the shell swaps its
  // provider on config change.
  const getTurnstileTokenRef = useRef(opts.getTurnstileToken);
  getTurnstileTokenRef.current = opts.getTurnstileToken;
  const renewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The preview's new-visitor quota is spent: every later handshake attempt (epoch bump, 401
  // recovery, online event) would be rejected again, so none is made for the rest of this mount.
  const visitorLimitHit = useRef(false);
  // Consecutive replies without a usable lifetime (drives the renew backoff).
  const renewStreak = useRef(0);
  // Absolute wall-clock timestamp (ms) for the next scheduled renew.
  // `setTimeout` pauses while the browser tab is suspended (sleep /
  // backgrounded with throttling), so the timer can be hours behind
  // the real expiry. On `visibilitychange` we compare against this
  // value to decide whether to handshake immediately.
  const nextRenewAt = useRef<number>(0);

  // Single handshake function reused for first-load + silent renew.
  // Silent renew reuses the same `visitorId` so the BE returns the
  // same `chatId` + `peopleId` — the visitor's session continuity is
  // preserved across token rotations.
  const performHandshake = useCallback(
    async (cancelled: { current: boolean }) => {
      // The preview token was rejected and its replacement has not arrived: a handshake would only
      // be rejected again. The new token re-runs the handshake effect.
      if (instance.preview.paused) {
        setSession((prev) => ({ ...prev, isAuthenticating: false }));
        return;
      }
      if (visitorLimitHit.current) {
        setSession((prev) => ({ ...prev, isAuthenticating: false, error: PREVIEW_VISITOR_LIMIT }));
        return;
      }
      // Flip `isAuthenticating` true synchronously so the shell sees
      // the in-flight state from the very first tick of the handshake
      // — without this, a visitor tap during Turnstile load races us
      // and slips through the launcher's open-gate. Also reset any
      // stale `error` from a previous failed attempt so a successful
      // retry doesn't keep the popup armed.
      setSession((prev) => ({ ...prev, isAuthenticating: true, error: null }));
      try {
        const visitorId = ensureVisitorId(instance.publicKey, instance.storage);
        const identity = instance.preview.active ? null : identityRef.current;
        // Visitor's resolved locale (BCP-47) rides along in `contextSnapshot`.
        // The BE prefers it over `Accept-Language` for the contact's LANGUAGE
        // profile field (only when that field is empty — never overwrites an
        // operator value). LOCATION is filled server-side from IP — nothing
        // to send here.
        const locale = detectClientLocale();
        // Resolve a Turnstile token BEFORE the mutation if the shell
        // wired up a provider. Provider returns `undefined` when
        // Turnstile is off for the integration; otherwise it loads
        // Cloudflare's script + runs the (usually invisible) challenge.
        // Any error here cancels the handshake with an actionable
        // error string — the visitor sees a "couldn't verify" hint
        // and the shell offers a retry.
        let turnstileToken: string | undefined;
        try {
          turnstileToken = (await getTurnstileTokenRef.current?.()) ?? undefined;
        } catch (err) {
          if (cancelled.current) return;
          setSession((prev) => ({
            ...prev,
            isAuthenticating: false,
            error: `turnstile_failed:${(err as Error).message}`,
          }));
          return;
        }
        if (cancelled.current) return;
        // Identity present → verified identified handshake via ONE proof:
        // a Google `id_token` (FedCM), a host-signed `jwt`, or the legacy
        // `userId`+`userHash` HMAC pair. Profile fields ride along; extra
        // attributes go as the `contextSnapshot` JSON. Otherwise anonymous.
        let out: HandshakeResult | undefined;
        if (
          identity &&
          (identity.googleIdToken || identity.jwt || (identity.userId && identity.userHash))
        ) {
          const res = await startIdentified({
            variables: {
              input: {
                visitorId,
                // exactly one proof — precedence: Google → jwt → HMAC.
                ...(identity.googleIdToken
                  ? { googleIdToken: identity.googleIdToken }
                  : identity.jwt
                    ? { jwt: identity.jwt }
                    : { userId: identity.userId, userHash: identity.userHash }),
                name: identity.name,
                email: identity.email,
                phone: identity.phone,
                contextSnapshot: JSON.stringify({
                  ...(identity.attributes && typeof identity.attributes === "object"
                    ? identity.attributes
                    : {}),
                  locale,
                }),
                turnstileToken,
              },
            },
          });
          if (cancelled.current) return;
          out = res.data?.startIdentifiedVisitor;
        } else {
          const res = await startAnonymous({
            variables: {
              input: { visitorId, turnstileToken, contextSnapshot: JSON.stringify({ locale }) },
            },
          });
          if (cancelled.current) return;
          out = res.data?.startAnonymousVisitor;
        }
        if (!out && instance.preview.paused) {
          // Rejected with a preview code: not a sign-in failure. Wait quietly for the new token.
          setSession((prev) => ({ ...prev, isAuthenticating: false, error: null }));
          return;
        }
        if (!out) {
          setSession((prev) => ({
            ...prev,
            isAuthenticating: false,
            error: "handshake_empty_response",
          }));
          return;
        }
        // Persist the BE-resolved visitorId if it ever differs from what we
        // sent. The BE no longer rotates (it resolves one stable row per
        // visitorId now), so this is normally a no-op — kept as a cheap
        // safety net to stay in lockstep with the server of record.
        if (out.visitorId && out.visitorId !== visitorId) {
          try {
            instance.storage.setItem(visitorIdKey(instance.publicKey), out.visitorId);
          } catch {
            /* private mode — best effort */
          }
        }
        instance.token = out.token;
        setSession({
          token: out.token,
          chatId: out.chatId,
          peopleId: out.peopleId,
          displayName: out.displayName,
          isAuthenticating: false,
          error: null,
          // The BE is authoritative — `kind` reflects whether the proof
          // actually verified, not just what we attempted.
          verified: out.kind === "identified",
          blocked: out.blocked ?? false,
        });
        // Persist a VERIFIED session so a reload restores it (instead of
        // re-handshaking anonymously → sign-in gate). Anonymous sessions
        // clear it so a logged-out reload stays anonymous.
        if (out.kind === "identified") {
          persistIdentifiedSession(
            {
              token: out.token,
              chatId: out.chatId,
              peopleId: out.peopleId,
              displayName: out.displayName,
              expiresIn: out.expiresIn,
            },
            instance.publicKey,
            instance.storage,
          );
        } else {
          clearPersistedIdentifiedSession(instance.publicKey, instance.storage);
        }
        // Schedule silent renew. `expiresIn` is seconds; we fire
        // ~30s before expiry so the new token replaces the old one
        // BEFORE any request 401s. A failed renew leaves the prior
        // token in place — it'll keep working until it actually
        // expires, at which point the session degrades gracefully.
        // `computeRenewDelay` guards a missing / NaN `expiresIn` (would be a 0 ms timer and an endless
        // re-handshake loop), a token shorter than the leeway, and a delay past the timer limit.
        const renew = computeRenewDelay(out.expiresIn, RENEW_LEEWAY_SECONDS, renewStreak.current);
        renewStreak.current = renew.streak;
        let renewInMs = renew.delayMs;
        // In a preview the JWT is capped by the preview token's own expiry, and each re-handshake with
        // the SAME token only gets the remainder (12 s, 7 s, 2 s...): ever faster renewals, then
        // WEB_PREVIEW_TOKEN_EXPIRED. A short lifetime means the token is nearly dead: ask the host for a
        // fresh one now (the new token re-runs the handshake) and never renew more often than 30 s.
        if (
          instance.preview.active &&
          typeof out.expiresIn === "number" &&
          out.expiresIn < PREVIEW_SHORT_JWT_SECONDS
        ) {
          instance.preview.requestRefresh();
          renewInMs = Math.max(PREVIEW_MIN_RENEW_MS, renewInMs);
        }
        if (renewTimer.current) clearTimeout(renewTimer.current);
        nextRenewAt.current = Date.now() + renewInMs;
        renewTimer.current = setTimeout(() => {
          if (!cancelled.current) performHandshake(cancelled);
        }, renewInMs);
      } catch (err) {
        if (cancelled.current) return;
        if (hasGuardCode(err as GuardErrorLike, WEB_GUARD_CODES.previewVisitorLimit)) {
          visitorLimitHit.current = true;
          setSession({
            token: null,
            chatId: null,
            peopleId: null,
            displayName: null,
            isAuthenticating: false,
            error: PREVIEW_VISITOR_LIMIT,
            verified: false,
            blocked: false,
          });
          return;
        }
        const quiet = instance.preview.paused;
        setSession({
          token: null,
          chatId: null,
          peopleId: null,
          displayName: null,
          isAuthenticating: false,
          // Classify: an unreachable BE (server down / network) is NOT a sign-in
          // failure — tag it so the shell shows the offline state + retries,
          // instead of the alarming "couldn't sign you in" overlay. Real auth
          // failures (4xx / bad response) keep their message → overlay shows.
          error: quiet ? null : isNetworkError(err) ? NETWORK_UNREACHABLE : (err as Error).message,
          verified: false,
          blocked: false,
        });
      }
    },
    [startAnonymous, startIdentified, instance],
  );

  // Stable key for the identity — flips when a (different) identity
  // arrives so the effect below re-handshakes. `null` while anonymous.
  // Covers every proof: a Google id_token, a signed `jwt`, or the
  // `userId:userHash` pair.
  const id = previewToken ? null : opts.identity;
  const identityKey = id?.googleIdToken
    ? `google:${id.googleIdToken}`
    : id?.jwt
      ? `jwt:${id.jwt}`
      : id?.userId && id?.userHash
        ? `${id.userId}:${id.userHash}`
        : null;

  // A forced re-handshake (`epoch` bump: shutdown / retry) must not leave the PREVIOUS visitor's
  // token, chat and person on the session while the new handshake is in flight: everything keyed on
  // them (thread list, live streams, profile) would keep serving the old identity for that whole
  // window. Declared BEFORE the handshake effect so its `isAuthenticating` update lands on top.
  const lastEpochRef = useRef(opts.epoch);
  useEffect(() => {
    if (lastEpochRef.current === opts.epoch) return;
    lastEpochRef.current = opts.epoch;
    renewStreak.current = 0;
    instance.token = null;
    setSession({
      token: null,
      chatId: null,
      peopleId: null,
      displayName: null,
      isAuthenticating: enabled,
      error: null,
      verified: false,
      blocked: false,
    });
  }, [opts.epoch, enabled, instance]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: identityKey is a re-fire trigger (read via identityRef inside performHandshake), not used directly in the body
  useEffect(() => {
    if (!enabled) {
      setSession((prev) => ({ ...prev, isAuthenticating: false }));
      return;
    }
    if (typeof window === "undefined") return;

    const cancelled = { current: false };
    // A new session (mount, identity change, epoch bump) never inherits the previous one's backoff.
    renewStreak.current = 0;
    // Restore a persisted VERIFIED session first, so a page reload keeps
    // the visitor signed in (instead of re-handshaking anonymously → the
    // sign-in gate). Skipped when the host supplied a fresh identity —
    // that re-handshakes identified and wins. There's no silent re-proof
    // for passkey/FedCM, so when the restored token expires we fall back
    // to a fresh (anonymous) handshake (the gate returns; re-sign-in).
    const restored =
      identityRef.current || previewToken
        ? null
        : readPersistedIdentifiedSession(instance.publicKey, instance.storage);
    if (restored) {
      instance.token = restored.token;
      setSession({
        token: restored.token,
        chatId: restored.chatId,
        peopleId: restored.peopleId,
        displayName: restored.displayName,
        isAuthenticating: false,
        error: null,
        verified: true,
        // Restored from a persisted token — block isn't stored; the threads
        // page + live stream re-establish it on this session's first fetch.
        blocked: false,
      });
      const expiresInMs = Math.min(MAX_TIMER_MS, Math.max(0, restored.expiresAt - Date.now()));
      nextRenewAt.current = restored.expiresAt;
      if (renewTimer.current) clearTimeout(renewTimer.current);
      renewTimer.current = setTimeout(() => {
        if (cancelled.current) return;
        clearPersistedIdentifiedSession(instance.publicKey, instance.storage);
        performHandshake(cancelled);
      }, expiresInMs);
    } else {
      // Re-runs when `identityKey` changes — e.g. the iframe receives the
      // host's identity over postMessage after the initial anonymous
      // handshake, upgrading the session to identified on the same chat.
      performHandshake(cancelled);
    }

    // Wake-from-sleep recovery — browsers pause `setTimeout` while a
    // tab is suspended (real sleep, OR aggressive background
    // throttling). The scheduled renew may be hours behind real time
    // by the time the user comes back; without this, the next send
    // 401s with `jwt expired` and the visitor sees a generic failure.
    // On `visibilitychange → visible`, if we've passed the scheduled
    // renew time we run a handshake immediately. The handshake itself
    // reschedules the timer.
    const onVisibility = () => {
      if (cancelled.current) return;
      if (typeof document === "undefined" || document.hidden) return;
      if (nextRenewAt.current === 0) return; // pre-first-handshake
      if (Date.now() >= nextRenewAt.current) {
        performHandshake(cancelled);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled.current = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (renewTimer.current) {
        clearTimeout(renewTimer.current);
        renewTimer.current = null;
      }
    };
    // `identityKey` re-fires the effect when the host identifies the
    // visitor (or swaps identity); `opts.epoch` re-fires it on shutdown
    // (after the stored visitorId was cleared → fresh anonymous identity).
    // `performHandshake` reads the latest identity from `identityRef`.
  }, [enabled, performHandshake, identityKey, opts.epoch, instance, previewToken]);

  // 401 recovery — Apollo's error link calls this when a NON-handshake
  // request is rejected as Unauthorized (stale / forged / expired /
  // tampered token). We drop the bad token + persisted session and
  // re-handshake: with `requireAuth` that lands on the sign-in gate so
  // the visitor can log in again; otherwise they get a fresh anonymous
  // token and can keep messaging. This is the "auto-logout on a bad
  // session" — without it a doctored `wexio:visitor-session` leaves the
  // widget stuck "verified" but unable to send OR re-login.
  // biome-ignore lint/correctness/useExhaustiveDependencies: opts.epoch is a re-run trigger so the cleanup cancels a recovery handshake left in flight by a shutdown
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    let recovering = false;
    // Flipped when the epoch bumps (shutdown / retry) or the effect tears down: a recovery
    // handshake still in flight then belongs to the PREVIOUS visitor and must not land its
    // token / session in the next one's.
    const cancelled = { current: false };
    instance.onUnauthorized = () => {
      if (recovering) return; // collapse a burst of concurrent 401s
      recovering = true;
      clearPersistedIdentifiedSession(instance.publicKey, instance.storage);
      instance.token = null;
      void performHandshake(cancelled).finally(() => {
        recovering = false;
      });
    };
    // `WEB_INTEGRATION_MISMATCH` — distinct from a generic 401. The BE
    // tells us the stored JWT was minted for a different integration
    // (cross-operator visitor whose legacy global localStorage key got
    // eager-migrated into THIS publicKey's slot by `ensureVisitorId` /
    // `readPersistedIdentifiedSession`). Self-heal by clearing the
    // scoped session AND the sticky visitorId for THIS publicKey, then
    // re-handshaking — the next handshake mints fresh, correctly-scoped
    // state. Without this we'd loop: token bad → 401 path drops it →
    // next mount re-migrates the same legacy key → same mismatch.
    instance.onIntegrationMismatch = () => {
      if (recovering) return;
      recovering = true;
      clearPersistedIdentifiedSession(instance.publicKey, instance.storage);
      instance.token = null;
      try {
        const pk = instance.publicKey;
        // Drop the sticky visitorId scoped to THIS publicKey too — the
        // visitorId was likely also migrated from the legacy key and
        // refers to the OTHER operator's People row.
        instance.storage.removeItem(visitorIdKey(pk));
      } catch {
        /* private mode — best effort */
      }
      void performHandshake(cancelled).finally(() => {
        recovering = false;
      });
    };
    return () => {
      cancelled.current = true;
      instance.onUnauthorized = null;
      instance.onIntegrationMismatch = null;
    };
    // `opts.epoch` re-creates the effect on shutdown so the cleanup above cancels a stale recovery.
  }, [enabled, opts.epoch, performHandshake, instance]);

  return session;
}

/**
 * Resolve the host-supplied visitor identity for the shell.
 *
 * Embeds (web-component / react-component) set `env.visitorIdentity`
 * directly — they run in the host's JS context. The iframe can't read
 * the host's data, so it receives identity over `postMessage`: this
 * hook announces readiness to the parent (`wexio:widget:ready:v1`) and
 * listens for `wexio:identify:v1`. Returns the env value when present,
 * else whatever arrived over the bridge (`null` until then).
 */
export function useHostVisitorData(env: WidgetEnv): {
  identity: VisitorIdentity | null;
  prefill: VisitorPrefill | null;
} {
  const [received, setReceived] = useState<{
    identity: VisitorIdentity | null;
    prefill: VisitorPrefill | null;
  }>({ identity: null, prefill: null });

  useEffect(() => {
    // Embeds pass these inline on `env`; only the iframe needs the bridge.
    if (env.visitorIdentity || env.visitorPrefill) return;
    if (typeof window === "undefined") return;

    const onMessage = (e: MessageEvent) => {
      if (!isTrustedMessageSource(e, env.kind)) return;
      const data = e.data;
      if (!data || typeof data !== "object") return;
      // Verified identity → identified handshake. Accept EITHER a signed
      // `jwt` (recommended) OR the legacy `userId`+`userHash` pair.
      if (data.type === "wexio:identify:v1") {
        const u = data.user;
        if (!u || typeof u !== "object") return;
        const hasGoogle = typeof u.googleIdToken === "string";
        const hasJwt = typeof u.jwt === "string";
        const hasHmac = typeof u.userId === "string" && typeof u.userHash === "string";
        if (!hasGoogle && !hasJwt && !hasHmac) return;
        setReceived((prev) => ({
          ...prev,
          identity: {
            googleIdToken: hasGoogle ? u.googleIdToken : undefined,
            jwt: hasJwt ? u.jwt : undefined,
            userId: typeof u.userId === "string" ? u.userId : undefined,
            userHash: typeof u.userHash === "string" ? u.userHash : undefined,
            name: typeof u.name === "string" ? u.name : undefined,
            email: typeof u.email === "string" ? u.email : undefined,
            phone: typeof u.phone === "string" ? u.phone : undefined,
            attributes: u.attributes && typeof u.attributes === "object" ? u.attributes : undefined,
          },
        }));
      } else if (data.type === "wexio:prefill:v1") {
        // Unverified profile → pre-fill / auto-attach the prechat.
        const p = data.prefill;
        if (!p || typeof p !== "object") return;
        setReceived((prev) => ({
          ...prev,
          prefill: {
            name: typeof p.name === "string" ? p.name : undefined,
            email: typeof p.email === "string" ? p.email : undefined,
            phone: typeof p.phone === "string" ? p.phone : undefined,
          },
        }));
      } else if (data.type === "wexio:shutdown:v1") {
        // Logout — forget the host-supplied identity/prefill so the
        // re-handshake (driven by the shell's epoch bump) goes anonymous.
        setReceived({ identity: null, prefill: null });
      }
    };
    window.addEventListener("message", onMessage);
    // Tell the loader we're ready so it replies with the host's identity +
    // prefill. (The loader also sends them proactively on iframe load —
    // this is the fast path / retry against a race.)
    window.parent?.postMessage({ type: "wexio:widget:ready:v1" }, "*");
    return () => window.removeEventListener("message", onMessage);
  }, [env.kind, env.visitorIdentity, env.visitorPrefill]);

  return {
    identity: env.visitorIdentity ?? received.identity,
    prefill: env.visitorPrefill ?? received.prefill,
  };
}

/**
 * Returns a stable visitor id from localStorage; mints one on first
 * visit. We use a uuid-v4-shaped random string rather than something
 * derived from fingerprinting — operators on privacy-sensitive sites
 * shouldn't see their visitors fingerprinted by the widget.
 *
 * Exported so the passkey sign-in (a separate ceremony, see
 * `usePasskeyAuth`) binds to the SAME sticky visitor id this handshake
 * uses — the BE then attaches the verified passkey to the visitor's
 * existing anonymous contact + chat (anon → identified continuity).
 */
export function ensureVisitorId(
  pk: string | null = getPublicKey(),
  storage: VisitorStorage = browserStorage("local"),
): string {
  try {
    const scopedKey = visitorIdKey(pk);
    const existing = storage.getItem(scopedKey);
    if (existing) return existing;
    // Migrate from the legacy global key on first read so a returning
    // visitor keeps their identity on the operator that wrote it. The
    // legacy key is removed in the same step so OTHER operators
    // sharing the same widget origin don't inherit it on their first
    // mount (the whole point of the scoping).
    if (pk) {
      const legacy = storage.getItem(LEGACY_VISITOR_ID_KEY);
      if (legacy) {
        storage.setItem(scopedKey, legacy);
        storage.removeItem(LEGACY_VISITOR_ID_KEY);
        return legacy;
      }
    }
    const fresh = generateVisitorId();
    storage.setItem(scopedKey, fresh);
    return fresh;
  } catch {
    // Privacy mode / cookies disabled → use an in-memory id. The
    // visitor loses chat continuity across reloads but the widget
    // still works for the current session.
    return generateVisitorId();
  }
}

function generateVisitorId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for older browsers — still 32 hex chars of entropy.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}
